import type { AppState, PageId } from '@/types'
import { GameEngine } from '@/game/GameEngine'
import { VoteManager } from '@/game/VoteManager'
import { WSClient } from '@/game/WSClient'
import { BombOverlay } from '@/components/BombOverlay'
import { PenaltyWheelOverlay } from '@/components/PenaltyWheelOverlay'
import { HomePage } from '@/pages/HomePage'
import { GamePage } from '@/pages/GamePage'
import { AboutPage } from '@/pages/AboutPage'
import { byIdMaybe } from '@/utils/dom'
import { templates } from '@/utils/template'

console.log(
  `%c👋 Hello, I'm Yunus\n\n%c一名前端出身的全栈开发者\n\n%c专注于高质量应用与用户体验\n探索 Web 技术 × 设计 × 开发\n\n👉 https://home.nofuzz.cn/`,
  
  "color: #00c6ff; font-size: 20px; font-weight: bold;",
  
  "color: #ff6b6b; font-size: 28px; font-weight: bold;",
  
  "color: #666; font-size: 14px;"
);

/**
 * App — the root controller.
 *
 * Responsibilities:
 *  - Own the single AppState instance
 *  - Mount all page controllers
 *  - Route navigation between pages
 *  - Coordinate game actions → engine → UI updates
 */
export class App {
  private state: AppState
  private ws: WSClient
  private overlay!: BombOverlay
  private penaltyWheel!: PenaltyWheelOverlay
  private gamePage!: GamePage
  private aboutPage!: AboutPage

  constructor(private root: HTMLElement) {
    this.state = {
      currentPage: 'home',
      roomName: '',
      roomId: '',
      game: GameEngine.createState(),
      votes: VoteManager.createState(),
      wsConnected: false,
      compactMode: false,
    }
    // Override to not-running initially (game starts on demand)
    this.state.game.isRunning = false

    this.ws = new WSClient()
  }

  /** Inject HTML, mount sub-controllers, show initial page */
  mount(): void {
    this.root.innerHTML =
      templates.home +
      templates.game +
      templates.about

    // Persistent overlay (lives outside pages)
    this.penaltyWheel = new PenaltyWheelOverlay()
    this.overlay = new BombOverlay((action) => {
      this.overlay.hide()
      if (action === 'new-game') this.startNewGame()
      if (action === 'penalty-wheel') this.penaltyWheel.show()
    })

    // Page controllers
    new HomePage(
      (name, id) => this.enterGame(name, id),
      (p) => this.navigate(p),
    )

    this.gamePage = new GamePage(
      (num) => this.callNumber(num),
      (p) => this.navigate(p),
      () => this.startNewGame(),
      (on) => this.toggleCompactMode(on),
    )
    // Mount the game page sub-components (grid + chart) once
    this.gamePage.mount(this.state.game)

    this.aboutPage = new AboutPage((p) => this.navigate(p))

    // Show home
    this.showPage('home')
  }

  // ─── Navigation ─────────────────────────────────────────────

  private navigate(page: PageId): void {
    if (this.state.currentPage === 'game' && page === 'home') {
      this.ws.disconnect()
      this.state.wsConnected = false
      this.gamePage.updateWsStatus(false, this.state.game.isRunning)
    }

    if (page === 'about') {
      this.aboutPage.updateGameBtn(!!this.state.roomId)
    }
    this.showPage(page)
  }

  private showPage(page: PageId): void {
    this.state.currentPage = page
    document.querySelectorAll<HTMLElement>('.page').forEach((el) => {
      el.classList.toggle('page--active', el.id === `page-${page}`)
    })
  }

  // ─── Enter game ──────────────────────────────────────────────

  private enterGame(roomName: string, roomId: string): void {
    this.state.roomName = roomName
    this.state.roomId = roomId

    // Update room label in header
    const label = byIdMaybe('header-room-label')
    if (label) label.textContent = `🎬 ${roomName} · ${roomId}`

    this.showPage('game')
    this.connectWS()
    this.gamePage.updateAll(this.state)
  }

  // ─── WebSocket ───────────────────────────────────────────────

  private connectWS(): void {
    this.ws.connect(
      this.state.roomId,
      (num) => this.handleBarrageNumber(num),
      (connected) => {
        this.state.wsConnected = connected
        this.gamePage.updateWsStatus(connected, this.state.game.isRunning)
      },
    )
  }

  private handleBarrageNumber(num: number): void {
    const g = this.state.game
    if (!g.isRunning || num < g.rangeMin || num > g.rangeMax) return
    VoteManager.addVote(this.state.votes, num)
    this.gamePage.updateVotes(this.state.votes.top3)
  }

  private toggleCompactMode(on: boolean): void {
    this.state.compactMode = on
    this.gamePage.setCompactMode(on)
  }

  // ─── Game actions ────────────────────────────────────────────

  private startNewGame(): void {
    // Reset game state
    this.state.game = GameEngine.createState()
    VoteManager.reset(this.state.votes)

    this.ws.startListening()

    // Start demo simulation if WS is not connected
    if (!this.state.wsConnected) {
      this.ws.startDemo(this.state.game.rangeMin, this.state.game.rangeMax)
    }

    // Re-mount grid with fresh state
    const gridEl = byIdMaybe('number-grid')
    if (gridEl) {
      // Re-initialize grid with new game state reference
      this.gamePage.mount(this.state.game)
    }

    this.gamePage.updateAll(this.state)
    this.gamePage.updateVotes([])

    console.info(`[Game] New round started. Bomb = ${this.state.game.bomb}`)
  }

  private callNumber(num: number): void {
    const result = GameEngine.callNumber(this.state.game, num)

    switch (result.type) {
      case 'invalid':
        this.gamePage.showCallError(result.reason)
        break

      case 'range_update':
        // Each valid call starts a fresh vote window for the next decision.
        VoteManager.reset(this.state.votes)
        this.ws.updateDemoRange(result.newMin, result.newMax)
        this.gamePage.updateAll(this.state)
        this.gamePage.updateVotes(this.state.votes.top3)
        break

      case 'bomb':
        this.ws.stopListening()
        VoteManager.reset(this.state.votes)
        this.gamePage.updateAll(this.state)
        this.gamePage.updateVotes(this.state.votes.top3)
        // Small delay so the grid update renders before overlay appears
        setTimeout(() => this.overlay.show(result.bomb), 260)
        break
    }
  }
}
